// Payments: who's owed for approved rounds, paying them (optionally adding the
// payment to the Finance page as an expense) and what's been paid so far.
import { useState } from 'react';
import { api } from '../lib/auth';
import { Button, Input, Select, Textarea, Checkbox, ModalShell } from '../lib/ui';
import { formatDay, formatGBP, todayYmd } from '../lib/trade';
import { personMoney, formatCount, PAY_METHODS } from '../lib/leaflets';
import { byId, PersonTag } from './LeafletsCommon';
import { Label, errorText } from './TradeCommon';

const RECORD_EXPENSE_KEY = 'farmos.leaflets.recordExpense';
const METHOD_LABEL = Object.fromEntries(PAY_METHODS.map(m => [m.key, m.label]));

export function PayModal({ person, data, today = todayYmd(), onClose, onPaid }) {
  const zones = byId(data.zones);
  const owed = data.jobs
    .filter(j => j.distributor === person._id)
    .flatMap(job => job.runs.filter(r => r.status === 'approved' && !r.payout).map(run => ({ job, run })))
    .sort((a, b) => String(a.run.date).localeCompare(String(b.run.date)));
  const [picked, setPicked] = useState(() => new Set(owed.map(o => o.run._id)));
  const [paidOn, setPaidOn] = useState(today);
  const [method, setMethod] = useState('bank_transfer');
  const [note, setNote] = useState('');
  const [record, setRecord] = useState(() => {
    try { return localStorage.getItem(RECORD_EXPENSE_KEY) !== '0'; } catch { return true; }
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const chosen = owed.filter(o => picked.has(o.run._id));
  const total = chosen.reduce((p, o) => p + Math.round(o.run.pay * 100), 0) / 100;
  const toggle = (id) => setPicked(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const pay = async () => {
    try { localStorage.setItem(RECORD_EXPENSE_KEY, record ? '1' : '0'); } catch { /* private mode */ }
    setSaving(true);
    setError('');
    try {
      const { data: res } = await api.post('/leaflets/admin/payouts', {
        distributor: person._id, runs: chosen.map(o => o.run._id), paidOn, method, note, recordExpense: record,
      });
      onPaid(res);
    } catch (e) {
      setError(errorText(e));
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} width={560} ariaLabel={`Pay ${person.name}`}>
      <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>Pay {person.name}</h2>
      <div className="trade-form">
        {owed.length ? (
          <ul className="lf-pick">
            {owed.map(({ job, run }) => (
              <li key={run._id}>
                <Checkbox checked={picked.has(run._id)} onChange={() => toggle(run._id)}
                  label={<>{formatDay(run.date)} <span className="lf-muted">{zones[job.zone]?.name}, {formatCount(run.delivered)} leaflets</span></>} />
                <b>{formatGBP(run.pay)}</b>
              </li>
            ))}
          </ul>
        ) : <p className="lf-muted">Nothing approved is waiting to be paid.</p>}
        <div className="inv-grid2">
          <div><Label>Paid on</Label><Input type="date" value={paidOn} max={today} onChange={e => setPaidOn(e.target.value || today)} /></div>
          <div>
            <Label>How</Label>
            <Select value={method} onChange={e => setMethod(e.target.value)}>
              {PAY_METHODS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
            </Select>
          </div>
        </div>
        <div><Label hint="optional">Note</Label><Textarea rows={1} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Monzo reference" /></div>
        <Checkbox checked={record} onChange={e => setRecord(e.target.checked)}
          label={`Add ${formatGBP(total)} to the Finance page as an expense (leaflet distribution)`} />
        <div className="trade-form__hint">Leave this off if the payment will come in with a bank statement import, so it isn't counted twice.</div>
        {error && <div className="ui-banner ui-banner--bad">{error}</div>}
      </div>
      <div className="trade-form__actions">
        <Button variant="muted" onClick={onClose}>Cancel</Button>
        <Button variant="success" onClick={pay} disabled={saving || !chosen.length}>{saving ? 'Saving…' : `Mark ${formatGBP(total)} paid`}</Button>
      </div>
    </ModalShell>
  );
}

export default function LeafletsPaymentsTab({ data, actions, dropPayout, putJobs, flash }) {
  const people = byId(data.distributors);
  const owing = data.distributors
    .map(p => ({ person: p, money: personMoney(p._id, data.jobs) }))
    .filter(x => x.money.owed > 0 || x.money.waiting > 0)
    .sort((a, b) => b.money.owed - a.money.owed);
  const owedTotal = owing.reduce((n, x) => n + Math.round(x.money.owed * 100), 0) / 100;

  const undo = async (payout) => {
    if (!window.confirm(`Undo the ${formatGBP(payout.amount)} payment to ${people[payout.distributor]?.name || 'them'}? Its rounds go back to waiting for payment${payout.financeTx ? ', and it comes off the Finance page' : ''}.`)) return;
    try {
      const { data: res } = await api.delete(`/leaflets/admin/payouts/${payout._id}`);
      dropPayout(payout._id);
      putJobs(res.jobs);
      flash('Payment undone', 'info');
    } catch (e) {
      flash(errorText(e), 'bad');
    }
  };

  return (
    <div className="lf-payments">
      <div className="ui-card">
        <h3>To pay {owedTotal > 0 && <span className="lf-muted">&middot; {formatGBP(owedTotal)}</span>}</h3>
        {owing.length ? (
          <ul className="trade-mini-list">
            {owing.map(({ person, money }) => (
              <li key={person._id} style={{ cursor: 'default' }}>
                <PersonTag person={person} onClick={() => actions.openPerson(person._id)} />
                <span className="lf-muted">
                  {money.owedRounds ? `${money.owedRounds} approved round${money.owedRounds === 1 ? '' : 's'}` : 'Nothing approved yet'}
                  {money.waiting > 0 && `, ${formatGBP(money.waiting)} waiting to be checked`}
                </span>
                <span className="amt">{formatGBP(money.owed)}</span>
                {money.owed > 0 && <Button size="sm" variant="primary" onClick={() => actions.pay(person)}>Pay</Button>}
              </li>
            ))}
          </ul>
        ) : <p className="lf-muted">Nobody's owed anything right now. Approved rounds add up here.</p>}
      </div>

      <div className="ui-card" style={{ padding: 0, overflowX: 'auto' }}>
        <h3 style={{ padding: '14px 16px 0' }}>Paid</h3>
        {data.payouts.length ? (
          <table className="trade-table">
            <thead><tr><th>Date</th><th>Who</th><th className="num">Rounds</th><th className="num">Amount</th><th>How</th><th /></tr></thead>
            <tbody>
              {data.payouts.map(p => (
                <tr key={p._id} style={{ cursor: 'default' }}>
                  <td>{formatDay(p.paidOn)}</td>
                  <td><PersonTag person={people[p.distributor]} />{p.note && <span className="sub">{p.note}</span>}</td>
                  <td className="num">{p.runs.length}</td>
                  <td className="num">{formatGBP(p.amount)}</td>
                  <td>{METHOD_LABEL[p.method] || p.method}{p.financeTx && <span className="sub">On the Finance page</span>}</td>
                  <td className="num"><Button size="sm" variant="muted" onClick={() => undo(p)}>Undo</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p className="lf-muted" style={{ padding: '6px 16px 16px' }}>No payments yet.</p>}
      </div>
    </div>
  );
}
